/**
 * DocsTab — Interactive API documentation & guide for users.
 * Matches the ManageApiTab design system (GitHub-dark card layout).
 */
import React, { useState } from 'react';

const API = 'https://data.abcz-tr.com';

/* ── Style constants ── */
const card: React.CSSProperties = {
  background: '#161b22',
  border: '1px solid #30363d',
  borderRadius: 10,
  padding: 24,
  marginBottom: 24,
};
const codeStyle: React.CSSProperties = {
  background: '#0d1117',
  borderRadius: 8,
  padding: 16,
  fontFamily: 'monospace',
  fontSize: 13,
  color: '#c9d1d9',
  overflowX: 'auto',
  lineHeight: 1.7,
};
const tagStyle = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: 'monospace',
  background: color + '18',
  color,
  border: `1px solid ${color}44`,
  marginRight: 6,
});

/* ── Syntax-highlighted code block (avoids JSX escaping hell) ── */
function Code({ text, copyText }: { text: string; copyText?: string }) {
  const [copied, setCopied] = useState(false);
  const doCopy = () => {
    const raw =
      copyText ??
      text
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(raw).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = raw;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
      document.body.removeChild(ta);
    }
  };
  const html = text
    .replace(
      /\b(import|from|as|def|return|if|not|for|in|raise)\b/g,
      '<span style="color:#ff7b72">$1</span>',
    )
    .replace(/(#[^\n]*)/g, '<span style="color:#8b949e">$1</span>')
    .replace(/"([^"]*?)"/g, '<span style="color:#a5d6ff">"$1"</span>')
    .replace(/'([^']*?)'/g, '<span style="color:#a5d6ff">\'$1\'</span>');
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={doCopy}
        style={{
          position: 'absolute',
          top: 8,
          right: 8,
          padding: '4px 10px',
          background: '#21262d',
          border: '1px solid #30363d',
          borderRadius: 6,
          color: copied ? '#3fb950' : '#8b949e',
          fontSize: 11,
          cursor: 'pointer',
          fontFamily: 'monospace',
          zIndex: 1,
        }}
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <pre
        style={{
          ...codeStyle,
          margin: 0,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

/* ── Collapsible section ── */
function Section({
  title,
  icon,
  defaultOpen = false,
  children,
}: {
  title: string;
  icon: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '16px 24px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 18 }}>{icon}</span>
        <span
          style={{ flex: 1, color: '#c9d1d9', fontSize: 15, fontWeight: 600 }}
        >
          {title}
        </span>
        <span
          style={{
            color: '#484f58',
            fontSize: 13,
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          &#9660;
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 24px 24px', borderTop: '1px solid #21262d' }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Table component ── */
function InfoTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}
      >
        <thead>
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  color: '#8b949e',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #21262d',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #21262d' }}>
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: '10px 10px',
                    color: '#c9d1d9',
                    fontSize: 13,
                    verticalAlign: 'top',
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Main component ── */
export default function DocsTab() {
  const [activeTab, setActiveTab] = useState<'guide' | 'python' | 'errors'>(
    'guide',
  );

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ color: '#c9d1d9', fontWeight: 600, margin: '0 0 6px' }}>
          API Documentation
        </h2>
        <p style={{ color: '#8b949e', fontSize: 14, margin: 0 }}>
          Learn how to pull Binance market data from the ClickHouse API — OHLCV,
          funding rates, open interest, long/short ratios, and more.
        </p>
      </div>

      {/* Tab pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        {[
          { key: 'guide' as const, label: 'Getting Started' },
          { key: 'python' as const, label: 'Python Examples' },
          { key: 'errors' as const, label: 'Errors & Limits' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '7px 18px',
              borderRadius: 20,
              border:
                activeTab === t.key
                  ? '1px solid #388bfd66'
                  : '1px solid #30363d',
              background: activeTab === t.key ? '#388bfd22' : 'transparent',
              color: activeTab === t.key ? '#58a6ff' : '#8b949e',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── Getting Started ─── */}
      {activeTab === 'guide' && (
        <>
          {/* Base URL + Auth */}
          <div style={card}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 16,
              }}
            >
              <div>
                <h3
                  style={{
                    color: '#c9d1d9',
                    margin: '0 0 8px',
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  Base URL & Authentication
                </h3>
                <p
                  style={{ color: '#8b949e', fontSize: 13, margin: '0 0 12px' }}
                >
                  All endpoints require an API key passed via the{' '}
                  <code style={{ color: '#79c0ff' }}>X-API-Key</code> header.
                  Generate one from the{' '}
                  <strong style={{ color: '#c9d1d9' }}>Manage API</strong> tab.
                </p>
              </div>
              <code
                style={{
                  background: '#0d1117',
                  padding: '8px 14px',
                  borderRadius: 6,
                  color: '#7ee787',
                  fontSize: 14,
                  fontFamily: 'monospace',
                  border: '1px solid #30363d',
                }}
              >
                {API}
              </code>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                marginTop: 8,
              }}
            >
              {[
                {
                  method: 'GET',
                  color: '#3fb950',
                  path: '/data/health',
                  note: 'ping',
                },
                {
                  method: 'POST',
                  color: '#58a6ff',
                  path: '/data',
                  note: 'JSON response',
                },
                {
                  method: 'POST',
                  color: '#58a6ff',
                  path: '/data/parquet',
                  note: 'ZIP download',
                },
                {
                  method: 'GET',
                  color: '#3fb950',
                  path: '/data',
                  note: 'URL params',
                },
              ].map((e, i) => (
                <div
                  key={i}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <span style={tagStyle(e.color)}>{e.method}</span>
                  <code style={{ color: '#8b949e', fontSize: 12 }}>
                    {e.path}
                  </code>
                  <span
                    style={{ color: '#484f58', fontSize: 12 }}
                  >{`\u2014 ${e.note}`}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Request Schema */}
          <Section title="Request Schema" icon="&#128221;" defaultOpen={true}>
            <p style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 4px' }}>
              Every data endpoint takes the same six required fields.
            </p>
            <InfoTable
              headers={['Field', 'Type', 'Allowed Values', 'Notes']}
              rows={[
                [
                  <code style={{ color: '#79c0ff' }}>coins</code>,
                  <code style={{ color: '#8b949e' }}>list[str]</code>,
                  <span style={{ fontSize: 12, color: '#c9d1d9' }}>
                    BTC, ETH, SOL, BNB, XRP, ADA, DOT, AVAX, LINK, ...
                  </span>,
                  <span style={{ fontSize: 12, color: '#8b949e' }}>
                    Base symbol only.{' '}
                    <code style={{ color: '#ffa657' }}>USDT</code> suffix added
                    automatically
                  </span>,
                ],
                [
                  <code style={{ color: '#79c0ff' }}>timeframes</code>,
                  <code style={{ color: '#8b949e' }}>list[str]</code>,
                  <span>
                    {'1m 5m 15m 30m 1h 4h 1d'.split(' ').map((t) => (
                      <span
                        key={t}
                        style={{ ...tagStyle('#8b949e'), marginBottom: 2 }}
                      >
                        {t}
                      </span>
                    ))}
                  </span>,
                  <span style={{ fontSize: 12, color: '#8b949e' }}>
                    Candle interval. All timeframes (1m/5m/15m/30m/1h/2h/4h/1d, futures + spot) are materialized, full history, hourly-refreshed — query any interval directly.
                  </span>,
                ],
                [
                  <code style={{ color: '#79c0ff' }}>data_types</code>,
                  <code style={{ color: '#8b949e' }}>list[str]</code>,
                  <span style={{ fontSize: 12 }}>
                    {'spot_ohlcv futures_ohlcv funding_rate open_interest global_ls_ratio top_ls_account top_ls_position taker_buy_sell'
                      .split(' ')
                      .map((t) => (
                        <div key={t} style={{ color: '#c9d1d9' }}>
                          {t}
                        </div>
                      ))}
                  </span>,
                  <span style={{ fontSize: 12, color: '#8b949e' }}>
                    Which tables to query
                  </span>,
                ],
                [
                  <code style={{ color: '#79c0ff' }}>data_source</code>,
                  <code style={{ color: '#8b949e' }}>str</code>,
                  <span>
                    <span style={tagStyle('#3fb950')}>cleaned</span>
                    <span style={tagStyle('#ffa657')}>spikes</span>
                  </span>,
                  <span style={{ fontSize: 12, color: '#8b949e' }}>
                    Which database
                  </span>,
                ],
                [
                  <code style={{ color: '#79c0ff' }}>date_from</code>,
                  <code style={{ color: '#8b949e' }}>str</code>,
                  <code style={{ color: '#a5d6ff', fontSize: 12 }}>
                    YYYY-MM-DD
                  </code>,
                  <span style={{ fontSize: 12, color: '#8b949e' }}>
                    Inclusive start
                  </span>,
                ],
                [
                  <code style={{ color: '#79c0ff' }}>date_to</code>,
                  <code style={{ color: '#8b949e' }}>str</code>,
                  <code style={{ color: '#a5d6ff', fontSize: 12 }}>
                    YYYY-MM-DD
                  </code>,
                  <span style={{ fontSize: 12, color: '#8b949e' }}>
                    Exclusive end, max 365 days
                  </span>,
                ],
              ]}
            />
          </Section>

          {/* Fetch JSON */}
          <Section title="Fetch JSON Data" icon="&#128230;" defaultOpen={true}>
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              <span style={tagStyle('#58a6ff')}>POST</span>{' '}
              <code style={{ color: '#c9d1d9' }}>/data</code> — Returns
              structured JSON with data, warnings, and per-type row_count.
            </p>
            <Code
              text={`# Fetch BTC + ETH 1h candles with funding rate
curl -s -X POST ${API}/data \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "coins": ["BTC", "ETH"],
    "timeframes": ["1h"],
    "data_types": ["spot_ohlcv", "funding_rate"],
    "data_source": "cleaned",
    "date_from": "2024-01-01",
    "date_to": "2024-01-08"
  }'`}
            />
            <p
              style={{
                color: '#8b949e',
                fontSize: 12,
                marginTop: 16,
                marginBottom: 8,
              }}
            >
              Response structure:
            </p>
            <Code
              text={`{
  "data": {
    "spot_ohlcv":  { "columns": [...], "rows": [...], "row_count": 168 },
    "funding_rate": { "columns": [...], "rows": [...], "row_count": 56 }
  },
  "warnings": []
}`}
            />
          </Section>

          {/* Download Parquet */}
          <Section title="Download Parquet Files" icon="&#128451;">
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              <span style={tagStyle('#58a6ff')}>POST</span>{' '}
              <code style={{ color: '#c9d1d9' }}>/data/parquet</code> — Same
              request body, returns a ZIP of Parquet files.
            </p>
            <Code
              text={`# Download as Parquet ZIP
curl -s -X POST ${API}/data/parquet \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"coins":["BTC"],"timeframes":["1h"],"data_types":["spot_ohlcv"],"data_source":"cleaned","date_from":"2024-01-01","date_to":"2024-01-08"}' \\
  -o data.zip`}
            />
            <p
              style={{
                color: '#8b949e',
                fontSize: 13,
                marginTop: 16,
                marginBottom: 8,
              }}
            >
              ZIP layout — at most two files per coin:
            </p>
            <Code
              text={`BTC/
  spot_ohlcv.parquet      # pure spot OHLCV
  derivatives.parquet     # all other types, merged on (symbol, interval, open_time)
ETH/
  spot_ohlcv.parquet
  derivatives.parquet`}
            />
            <p style={{ color: '#484f58', fontSize: 12, marginTop: 12 }}>
              In derivatives.parquet, columns are prefixed by data type:
              funding_rate__rate, open_interest__sum_open_interest, etc.
            </p>
          </Section>

          {/* NEW 2026-06: direct SQL for raw + microstructure datasets */}
          <Section
            title="Direct SQL — raw, microstructure & new datasets (/ch)"
            icon="&#128190;"
          >
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              The <code style={{ color: '#c9d1d9' }}>/data</code> endpoint above
              serves the processed <b>cleaned / spike</b> OHLCV + derivatives.
              For <b>raw</b> data and the newer large / tick-level datasets,
              query ClickHouse directly via{' '}
              <span style={tagStyle('#58a6ff')}>POST</span>{' '}
              <code style={{ color: '#c9d1d9' }}>/ch</code> with SQL.
            </p>
            <Code
              text={`# Any table in binance_raw / external - e.g. 1m premium index (basis)
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT open_time, close FROM binance_raw.premium_index_1m
   WHERE symbol='BTCUSDT' AND open_time>='2025-01-01'
   ORDER BY open_time FORMAT JSONEachRow"

# Crypto implied-vol index (Deribit DVOL = BTC/ETH, the crypto VIX)
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT date, btc_dvol, eth_dvol FROM external.deribit_dvol ORDER BY date DESC LIMIT 30 FORMAT JSONEachRow"

# Gold spot XAUUSD 1m (Dukascopy bid/ask) - pair with PAXG (binance_raw.spot_ohlcv_1m) for a gold basis
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT timestamp, mid_close FROM external.xauusd_1m ORDER BY timestamp DESC LIMIT 30 FORMAT JSONEachRow"

# Protocol fees / revenue (DefiLlama P/F fundamentals) - top protocols by daily fees
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT protocol, timestamp, daily_fees, daily_revenue FROM external.defillama_fees
   WHERE timestamp>='2026-06-01' ORDER BY daily_fees DESC LIMIT 30 FORMAT JSONEachRow"

# Cross-exchange aggregated OI / funding / long-short ratio (Coinalyze)
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT date, asset, oi_usd, funding_rate, long_short_ratio FROM external.coinalyze_metrics ORDER BY date DESC LIMIT 30 FORMAT JSONEachRow"

# Deribit 25-delta risk-reversal skew (options directional positioning, BTC/ETH x expiry bucket)
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT date, ccy, expiry_bucket, skew FROM external.theblock_skew ORDER BY date DESC LIMIT 30 FORMAT JSONEachRow"

# Deribit per-strike DAILY implied vol (free Tardis alternative, BS-derived from expired-option OHLCV)
curl -s "${API}/ch" -H "X-API-Key: YOUR_KEY" --data-binary \\
  "SELECT * FROM external.deribit_options_iv_daily ORDER BY date DESC LIMIT 30 FORMAT JSONEachRow"`}
            />
            <p
              style={{
                color: '#8b949e',
                fontSize: 12,
                marginTop: 16,
                marginBottom: 8,
              }}
            >
              New datasets queryable via /ch (added 2026-06):
            </p>
            <Code
              text={`binance_raw.premium_index_1m   # 1m premium index (basis), 2020-01+, 29 syms
binance_raw.agg_trades         # tick aggTrades BTC/ETH (transact_time, price, qty, is_buyer_maker)
binance_raw.book_ticker        # best bid/ask BTC/ETH (transaction_time, best_bid/ask_price/qty)
binance_raw.book_depth         # top-of-book depth MAJORS8 (timestamp, percentage, depth, notional)
external.bybit_trades          # Bybit trades BTC/ETH (timestamp, side, size, price)
external.okx_klines            # OKX SWAP 1m klines (inst_id, open_time, OHLCV)
external.deribit_dvol          # BTC/ETH implied-vol index (date, btc_dvol, eth_dvol)
external.deribit_options       # full option chain (IV / greeks / OI)
external.macro_calendar        # FOMC event dates
external.xauusd_1m             # XAUUSD gold spot 1m bid/ask (Dukascopy tick->1m, 2003+) - pair w/ PAXG spot for gold basis
external.defillama_fees        # protocol daily fees + revenue (protocol, timestamp, daily_fees, daily_revenue), 2018-08+
external.defillama_dexvol      # per-protocol DEX volume USD (protocol, chains, timestamp, volume_usd), 2016-04+
external.defillama_yields      # pool APY (pool_id, project, symbol, tvl_usd, apy, apy_base, apy_reward)
external.defillama_tvl         # protocol/chain TVL history (chain, timestamp, tvl_usd), 2017-09+
external.treasury_tga          # US Treasury daily cash / TGA - net-liquidity (record_date, account_type, close_today_bal), 2005-10+
external.polymarket            # prediction-market event odds (question, outcome, timestamp, price, volume_num)
external.wiki_pageviews        # Wikipedia daily pageviews / attention (article, timestamp, views), 2015-07+
external.deribit_funding       # Deribit perp funding + index (instrument, timestamp, index_price, interest_8h, interest_1h), 2020-01+
external.exchange_oi           # Bybit/OKX/Bitget open interest (exchange, symbol, timestamp, open_interest, open_interest_usd)
external.hl_asset_ctxs         # Hyperliquid asset ctx (coin, funding, open_interest, mark_px, premium, day_ntl_vlm)
external.deribit_options_hist  # Deribit option-chain history - hourly per-strike IV/greeks (symbol, strike_price, mark_iv, delta, gamma, vega, theta, open_interest), 2020-03+
external.coinalyze_metrics     # cross-exchange OI/funding/long-short (asset, oi_usd, funding_rate, long_pct, long_short_ratio), 2020+
external.binance_perp_aggtrades # Binance USD-M perp tick -> 1m OHLCV+CVD+large-print flags, 8 majors full history, 2020-01+
external.okx_trades            # OKX USDT-perp tick -> 1m OHLCV+CVD+large-print (symbol, minute, ohlcv, cvd, large_*), 2021-10+
external.gate_trades           # Gate.io USDT-perp tick -> 1m OHLCV+CVD+large-print, 2018+
external.theblock_skew         # Deribit 25-delta risk-reversal skew (date, ccy, expiry_bucket, skew), BTC+ETH x 5 buckets, 2020-01+
external.theblock_atm_iv       # ATM implied-vol term structure (date, ccy, tenor, atm_iv) 7/30/90/180d, BTC+ETH, 2020-07+
external.theblock_var_premium  # IV-RV variance premium = DVOL - Parkinson HV (date, ccy, var_premium, dvol, parkinson_hv), 2021-04+
external.deribit_options_iv_daily # Deribit per-strike DAILY implied vol, BS-derived from expired-option OHLCV (free Tardis alternative)
external.okx_options_hist      # OKX option-chain history (Tardis free monthly, hourly per-strike IV/greeks: strike/mark_iv/delta/gamma/vega/oi), 2020-06+
external.bybit_options_hist    # Bybit option-chain history (Tardis free monthly, hourly per-strike IV/greeks), 2023-04+
external.binance_eu_options_hist # Binance-EU option-chain history (Tardis free monthly, hourly per-strike IV/greeks), 2023-02+
external.loris_funding         # cross-exchange + DEX-perp current funding, 34 venues incl Lighter/Vest/Hyperliquid (exchange, symbol, funding_rate, ts), hourly
external.github_dev            # crypto repo dev activity (repo, stars, forks, open_issues, commits, contributors), daily
external.github_releases       # crypto repo release history (repo, tag_name, published_at), multi-year (go-ethereum 2014+, solana 521)
external.gtrends               # Google Trends search interest for crypto terms (term, date, interest 0-100)
external.reddit                # daily crypto subreddit submission activity (subreddit, day, n_submissions, sum_score, sum_comments)
external.cex_onchain_flow      # zero-lag CEX on-chain flows across BTC/ETH/BNB/SOL/XRP + tron/USDT/USDC/WBTC (date, entity, chain, asset, amount_in/out, net_amount, transfers, obs_ts), 2020+
external.cex_reserve           # VIEW: cumulative CEX reserve per (entity, chain, asset) = running sum of net flows
external.cex_addresses         # open-source CEX EVM address labels (address, entity, wallet_name), 4957 addrs / 328 entities, weekly sync
external.cex_addresses_btc     # CEX bitcoin address labels (address, entity, wallet_name), 761 addrs / 53 entities
external.cex_labels            # open-source CEX address labels from spellbook GitHub (blockchain, address, cex_name, distinct_name), 8069 addrs / 16 chains (evm+btc+sol+xrp+tron+ltc...), monthly refresh
external.funding_by_venue      # per-venue daily funding (date, coin, exchange, funding, n_perps), 17 venues x 6 coins, 2020-04+
external.oi_by_venue           # per-venue daily open interest USD (date, coin, exchange, oi_usd, n_perps), 2022-05+
external.ls_ratio_by_venue     # long/short account ratio by venue (date, coin, exchange, long_ratio), OKX 180d / Bybit 2020-08+ / Binance 30d
external.token_unlocks         # forward token-unlock calendar (token, unlock_date, amount, pct_circulating, category), 133 tokens, 10y horizon, daily refresh
external.defi_lending_stress   # DeFi lending utilization (date, pool, project, chain, supply/borrow usd, utilization, borrow_apy, ltv), ~2.3k pools daily
external.defi_liq_cliff        # liquidation cliff: liquidatable collateral by % distance from spot (date, asset, protocol, chain, pct_bucket, liquidatable_usd), eth/btc/sol/avax/matic/bnb
external.hyperliquid_premium   # VIEW: Hyperliquid perp premium / mark / oracle per coin, daily (from hl_asset_ctxs)
external.hyperliquid_whale_pos # top active Hyperliquid whale positions (date, addr, coin, szi signed, entry_px, position_value, unrealized_pnl, leverage), daily
external.macro_calendar        # macro event calendar FOMC/CPI/NFP/PPI/GDP/PCE (event_date, event, time_utc), forward schedule, weekly auto-refresh`}
            />
            <p style={{ color: '#484f58', fontSize: 12, marginTop: 12 }}>
              Any binance_raw / external table is queryable. Append FORMAT
              JSONEachRow / CSVWithNames / Parquet. The export UI (Home) also
              lists every dataset.
            </p>
          </Section>

          {/* All datasets — catalog + generic download (external + any table) */}
          <Section
            title="All Datasets — Catalog & Download (external macro / on-chain / cross-exchange / gold)"
            icon="&#127760;"
          >
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              The <code style={{ color: '#c9d1d9' }}>/data</code> endpoints
              above serve Binance OHLCV by coins/timeframes. To list and
              download EVERY dataset - all 72 external tables (on-chain
              valuation, protocol fees / revenue / TVL / yields / DEX-volume,
              macro & treasury / net-liquidity, cross-exchange funding &
              open-interest, options chain + IV history, prediction markets,
              attention, sentiment, gold) plus any raw table - as JSON / CSV /
              Parquet, use these (same X-API-Key):
            </p>
            <Code
              text={`# Discover every downloadable dataset (databases -> tables, rows, columns)
curl -s "${API}/data/catalog" -H "X-API-Key: YOUR_KEY"

# List the external sources (on-chain / macro / cross-exchange / options / gold)
curl -s "${API}/data/external/sources" -H "X-API-Key: YOUR_KEY"

# Download one external source as Parquet
curl -s "${API}/data/external/coinmetrics?format=parquet" \\
  -H "X-API-Key: YOUR_KEY" -o coinmetrics.parquet

# Download ANY table with optional date filter (format=json|csv|parquet)
curl -s "${API}/data/table/external/funding_rates?format=parquet&start=2026-01-01&end=2026-06-01" \\
  -H "X-API-Key: YOUR_KEY" -o funding_rates.parquet

# Microstructure / new Binance tables download here too
curl -s "${API}/data/table/binance_raw/agg_trades?format=parquet&start=2026-06-01&end=2026-06-10" \\
  -H "X-API-Key: YOUR_KEY" -o agg_trades.parquet`}
            />
            <p style={{ color: '#484f58', fontSize: 12, marginTop: 12 }}>
              Params: format (json|csv|parquet, default json), start / end
              (YYYY-MM-DD date filter), limit. <strong>Big tables</strong> (e.g.
              bybit_trades, agg_trades, book_ticker — billions of rows){' '}
              <strong>require a start/end date range</strong>: a whole-table
              download is rejected with a 400 telling you to narrow it (it would
              otherwise time out). Small/medium tables download whole fine.
            </p>
            <p style={{ color: '#484f58', fontSize: 12, marginTop: 6 }}>
              Note: the bulk{' '}
              <code style={{ color: '#8b949e' }}>POST /data</code> &amp;{' '}
              <code style={{ color: '#8b949e' }}>/data/parquet</code> endpoints
              are for <strong>Binance coins×timeframes only</strong> (and need{' '}
              <code style={{ color: '#8b949e' }}>data_source</code> ={' '}
              <code style={{ color: '#3fb950' }}>cleaned</code> or{' '}
              <code style={{ color: '#3fb950' }}>spikes</code> — not "raw"). To
              download an <strong>external</strong> dataset use the{' '}
              <code style={{ color: '#c9d1d9' }}>
                /data/external/&#123;source&#125;
              </code>{' '}
              or{' '}
              <code style={{ color: '#c9d1d9' }}>
                /data/table/external/&#123;table&#125;
              </code>{' '}
              paths above (json/csv/parquet) — there is no /external/parquet
              route.
            </p>
          </Section>

          {/* Wish list — request datasets to be collected */}
          <Section
            title="Wish List — Request a Dataset to be Collected"
            icon="&#128221;"
          >
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              Want data that is not here yet? Record it on the wish list and we
              will plan its collection.{' '}
              <strong>
                Please include a download{' '}
                <code style={{ color: '#c9d1d9' }}>source_url</code> — a FREE
                source is strongly preferred
              </strong>{' '}
              (it gets collected far faster). You can only add a wish; this
              never modifies any data.
            </p>
            <Code
              text={`# Submit a wish (only "dataset" is required; a free source_url helps a lot)
curl -s -X POST "${API}/data/wishlist" -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" -d '{
    "dataset": "Deribit BTC option order book L2",
    "description": "per-instrument bids/asks, top 10 levels",
    "source_url": "https://datasets.tardis.dev/...   # free archive preferred",
    "source_is_free": true,
    "cadence": "1m snapshots, 2y history",
    "priority": "normal"
  }'

# See the wish list (optionally filter: ?status=pending|planned|collecting|done|rejected)
curl -s "${API}/data/wishlist" -H "X-API-Key: YOUR_KEY"`}
            />
            <p style={{ color: '#484f58', fontSize: 12, marginTop: 12 }}>
              New wishes start as{' '}
              <code style={{ color: '#c9d1d9' }}>pending</code>. Status moves
              pending → planned → collecting → done as we work it. The more
              concrete the dataset + a free source_url, the quicker it lands in
              the catalog above.
            </p>
          </Section>

          {/* GET shortcut */}
          <Section title="GET Shortcut (URL Params)" icon="&#128279;">
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              <span style={tagStyle('#3fb950')}>GET</span>{' '}
              <code style={{ color: '#c9d1d9' }}>/data</code> — Same data via
              query parameters. Useful for browser access and shareable links.
            </p>
            <Code
              text={`${API}/data?coins=BTC,ETH&timeframes=1h&data_types=spot_ohlcv,funding_rate&data_source=cleaned&date_from=2024-01-01&date_to=2024-01-02`}
            />
          </Section>
        </>
      )}

      {/* ─── Python Examples ─── */}
      {activeTab === 'python' && (
        <>
          <Section
            title="JSON to DataFrame"
            icon="&#128202;"
            defaultOpen={true}
          >
            <Code
              text={`import requests, pandas as pd

r = requests.post("${API}/data",
    headers={"X-API-Key": "YOUR_KEY"},
    json={
        "coins": ["BTC", "ETH"],
        "timeframes": ["1h"],
        "data_types": ["spot_ohlcv"],
        "data_source": "cleaned",
        "date_from": "2024-01-01",
        "date_to": "2024-01-08",
    }
)
r.raise_for_status()
payload = r.json()

df = pd.DataFrame(payload["data"]["spot_ohlcv"]["rows"])
print(df.shape, list(df.columns))`}
            />
          </Section>

          <Section
            title="Parquet ZIP to DataFrames"
            icon="&#128230;"
            defaultOpen={true}
          >
            <Code
              text={`import io, zipfile, requests, pandas as pd

r = requests.post("${API}/data/parquet",
    headers={"X-API-Key": "YOUR_KEY"},
    json={
        "coins": ["BTC", "ETH"],
        "timeframes": ["1h"],
        "data_types": ["spot_ohlcv", "funding_rate"],
        "data_source": "cleaned",
        "date_from": "2024-01-01",
        "date_to": "2024-01-08",
    }
)
r.raise_for_status()

z = zipfile.ZipFile(io.BytesIO(r.content))
print(z.namelist())  # ['BTC/spot_ohlcv.parquet', ...]

dfs = {name: pd.read_parquet(io.BytesIO(z.read(name))) for name in z.namelist()}
btc_spot = dfs["BTC/spot_ohlcv.parquet"]`}
            />
          </Section>

          <Section title="Reusable Client Helper" icon="&#128295;">
            <Code
              text={`import io, zipfile, requests, pandas as pd

BASE = "${API}"
HEADERS = {"X-API-Key": "YOUR_KEY"}

def fetch_json(coins, timeframes, data_types, source, date_from, date_to):
    r = requests.post(f"{BASE}/data", headers=HEADERS, json={
        "coins": coins, "timeframes": timeframes, "data_types": data_types,
        "data_source": source, "date_from": date_from, "date_to": date_to,
    })
    if not r.ok:
        raise RuntimeError(r.json())
    return r.json()

def fetch_parquet(coins, timeframes, data_types, source, date_from, date_to):
    r = requests.post(f"{BASE}/data/parquet", headers=HEADERS, json={
        "coins": coins, "timeframes": timeframes, "data_types": data_types,
        "data_source": source, "date_from": date_from, "date_to": date_to,
    })
    r.raise_for_status()
    z = zipfile.ZipFile(io.BytesIO(r.content))
    return {name: pd.read_parquet(io.BytesIO(z.read(name))) for name in z.namelist()}`}
            />
          </Section>
        </>
      )}

      {/* ─── Errors & Limits ─── */}
      {activeTab === 'errors' && (
        <>
          <Section
            title="Error Response Format"
            icon="&#128680;"
            defaultOpen={true}
          >
            <p
              style={{ color: '#8b949e', fontSize: 13, margin: '16px 0 12px' }}
            >
              All errors return a consistent JSON envelope:
            </p>
            <Code
              text={`{
  "error": {
    "code": "VALIDATION_INVALID_COINS",
    "message": "Invalid coins: ['FAKECOIN']",
    "status": 400,
    "retryable": false,
    "details": { "invalid": ["FAKECOIN"], "allowed": [...] }
  }
}`}
            />
          </Section>

          <Section
            title="HTTP Status Codes"
            icon="&#128214;"
            defaultOpen={true}
          >
            <InfoTable
              headers={['Status', 'When']}
              rows={[
                [<span style={tagStyle('#3fb950')}>200</span>, 'Success'],
                [
                  <span style={tagStyle('#ffa657')}>400</span>,
                  'Validation failed — bad coin, timeframe, data type, source, date format/order/range',
                ],
                [
                  <span style={tagStyle('#ffa657')}>422</span>,
                  'Request body schema invalid — missing field, wrong type (Pydantic)',
                ],
                [
                  <span style={tagStyle('#ffa657')}>404</span>,
                  '/data/parquet only — no rows matched any filter',
                ],
                [
                  <span style={tagStyle('#f85149')}>503</span>,
                  'ClickHouse unreachable, query failed after retries, or all data types failed',
                ],
                [
                  <span style={tagStyle('#f85149')}>500</span>,
                  'Unexpected internal error',
                ],
              ]}
            />
          </Section>

          <Section
            title="Error Codes Reference"
            icon="&#128209;"
            defaultOpen={true}
          >
            <InfoTable
              headers={['Code', 'Meaning']}
              rows={[
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_INVALID_COINS
                  </code>,
                  'One or more coins not in the whitelist',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_INVALID_TIMEFRAMES
                  </code>,
                  'Bad timeframe value',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_INVALID_DATA_TYPES
                  </code>,
                  'Bad data type name',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_INVALID_DATA_SOURCE
                  </code>,
                  'Must be cleaned or spikes',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_INVALID_DATE_FORMAT
                  </code>,
                  'Dates must be YYYY-MM-DD',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_DATE_RANGE
                  </code>,
                  'date_from must be before date_to',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    VALIDATION_DATE_RANGE_TOO_LARGE
                  </code>,
                  'Range exceeds 365 days',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    DB_UNAVAILABLE
                  </code>,
                  "ClickHouse can't be reached",
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    DB_QUERY_FAILED
                  </code>,
                  'Query errored (not retryable)',
                ],
                [
                  <code style={{ color: '#79c0ff', fontSize: 12 }}>
                    DATA_PARTIAL_FAILURE
                  </code>,
                  'Every requested data type failed',
                ],
              ]}
            />
          </Section>

          <Section title="Limits & Gotchas" icon="&#9888;">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginTop: 16,
              }}
            >
              {[
                {
                  title: 'Row Limit',
                  desc: '10,000 rows per data type per request. If you hit it, narrow your date range.',
                  color: '#ffa657',
                },
                {
                  title: 'Date Range',
                  desc: 'Maximum 365 days per request. Split longer ranges into multiple calls.',
                  color: '#ffa657',
                },
                {
                  title: 'Symbol Format',
                  desc: 'Rows always return with USDT suffix (BTCUSDT). Pass the base symbol only (BTC).',
                  color: '#58a6ff',
                },
                {
                  title: 'Funding Rate Time',
                  desc: 'Uses funding_time in JSON, renamed to open_time in Parquet for clean merging.',
                  color: '#58a6ff',
                },
                {
                  title: 'Warnings Array',
                  desc: 'Always check warnings[] — it surfaces 0-row results, truncation, and partial failures.',
                  color: '#3fb950',
                },
                {
                  title: 'Partial Failures',
                  desc: 'If one data type fails, others still return. Only all-fail triggers an error response.',
                  color: '#3fb950',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  style={{
                    background: '#0d1117',
                    border: '1px solid #21262d',
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: item.color,
                        display: 'inline-block',
                      }}
                    />
                    <span
                      style={{
                        color: '#c9d1d9',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {item.title}
                    </span>
                  </div>
                  <p
                    style={{
                      color: '#8b949e',
                      fontSize: 12,
                      margin: 0,
                      lineHeight: 1.5,
                    }}
                  >
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
